import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { Input, Form, Modal, Typography, Switch, Spin } from "antd";

import CustomSelect from "@/components/inputs/CustomSelect";

import {
  createItem,
  updateItem,
  getDefinitionOptions,
  addDefinitionTemplates,
  deleteDefinitionTemplates,
} from "@/api/definitions";

import { getKeyCategoriesOptions } from "@/api/nspd";

import {
  MODAL_TITLES,
  MODEL_BY_PATH,
  WITHOUT_TEMPLATES,
} from "@/constants/definitions";

const { Text } = Typography;

const NSPD_CADASTRE_FIELD_TYPES = ["text", "mask"];

const getTemplateUuids = (items) =>
  (items || [])
    .map((item) => (typeof item === "string" ? item : item?.uuid))
    .filter(Boolean);

const getTemplateDiff = (initialTemplates = [], nextTemplates = []) => {
  const initialUuids = getTemplateUuids(initialTemplates);
  const nextUuids = getTemplateUuids(nextTemplates);

  return {
    added: nextUuids.filter((uuid) => !initialUuids.includes(uuid)),
    deleted: initialUuids.filter((uuid) => !nextUuids.includes(uuid)),
  };
};

const getDeselectTemplateUuid = (value) =>
  typeof value === "string" ? value : value?.value || value?.uuid;

const DefinitionModal = ({
  data,
  visible,
  loading = false,
  onSave,
  onCancel,
}) => {
  const { type } = useParams();

  const [form] = Form.useForm();
  const useNSPD = Form.useWatch("is_nspd_enabled", form);
  const selectedTemplates = Form.useWatch("templates", form);

  const isClearingTemplatesRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [keyCategories, setKeyCategories] = useState([]);

  const isEdit = Boolean(data?.uuid);

  const objectFields = data?.fields?.self || data?.fields || [];

  const cadastreFieldOptions = objectFields
    .filter((field) => NSPD_CADASTRE_FIELD_TYPES.includes(field.type))
    .map((field) => ({
      ...field,
      label: field.name,
      value: field.uuid,
    }));

  const initialUseNSPD = Boolean(data?.is_nspd_enabled);
  const showDisableNSPDWarning = initialUseNSPD && useNSPD === false;

  const getTemplateName = (uuid) =>
    templates.find((item) => item.uuid === uuid)?.name ||
    data?.templates?.find((item) => item.uuid === uuid)?.name ||
    "выбранный шаблон";

  const isAttachedTemplate = (uuid) =>
    getTemplateUuids(data?.templates).includes(uuid);

  const restoreTemplates = (items) => {
    setTimeout(() => {
      const currentUuids = getTemplateUuids(form.getFieldValue("templates"));
      const restoreUuids = getTemplateUuids(items);

      form.setFieldValue("templates", [
        ...new Set([...currentUuids, ...restoreUuids]),
      ]);
    }, 0);
  };

  const confirmTemplateDetach = ({ title, content, onCancel }) => {
    Modal.confirm({
      title,
      content,
      okText: "Открепить",
      okType: "danger",
      cancelText: "Отмена",
      onCancel,
    });
  };

  const handleTemplateDeselect = (value) => {
    if (isClearingTemplatesRef.current) return;

    const uuid = getDeselectTemplateUuid(value);

    if (!uuid || !isAttachedTemplate(uuid)) return;

    confirmTemplateDetach({
      title: "Открепить шаблон?",
      content: `При откреплении шаблона «${getTemplateName(uuid)}» данные по его шаблонным полям будут очищены. Это действие нельзя будет отменить.`,
      onCancel: () => restoreTemplates([uuid]),
    });
  };

  const handleTemplatesClear = () => {
    const currentTemplates = getTemplateUuids(selectedTemplates);

    const attachedTemplates = currentTemplates.filter(isAttachedTemplate);

    if (!attachedTemplates.length) return;

    isClearingTemplatesRef.current = true;
    setTimeout(() => {
      isClearingTemplatesRef.current = false;
    }, 0);

    confirmTemplateDetach({
      title: "Открепить все шаблоны?",
      content:
        "При откреплении шаблонов данные по их шаблонным полям будут очищены. Это действие нельзя будет отменить.",
      onCancel: () => restoreTemplates(currentTemplates),
    });
  };

  const fetchTemplateList = async () => {
    try {
      const options = await getDefinitionOptions({
        type: "template",
      });

      setTemplates(
        options.map((item) => ({
          ...item,
          label: item.name,
        })),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const fetchKeyCategories = async () => {
    try {
      const items = await getKeyCategoriesOptions();

      setKeyCategories(
        items.map((item) => ({
          ...item,
          label: item.name,
          value: item.uuid,
        })),
      );
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchTemplateList();
    fetchKeyCategories();
  }, [type]);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        ...data,
        templates: getTemplateUuids(data?.templates),
        is_nspd_enabled: Boolean(data?.is_nspd_enabled),
        nspd_category_uuid: data?.nspd_category_uuid || undefined,
        nspd_cadastral_field_uuid: data?.nspd_cadastral_field_uuid || undefined,
      });
    } else {
      form.resetFields();
    }
  }, [visible, data, form]);

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const saveTemplates = async (uuid, nextTemplates) => {
    const { added, deleted } = getTemplateDiff(data?.templates, nextTemplates);

    if (added.length) {
      await addDefinitionTemplates({ uuid, templates: added });
    }

    if (deleted.length) {
      await deleteDefinitionTemplates({ uuid, templates: deleted });
    }
  };

  const handleSave = async (values) => {
    setError(null);
    setIsSaving(true);

    const { templates: nextTemplates = [], ...definitionValues } = values;

    const payload = {
      ...definitionValues,
      nspd_category_uuid: definitionValues.is_nspd_enabled
        ? definitionValues.nspd_category_uuid
        : null,
      nspd_cadastral_field_uuid: definitionValues.is_nspd_enabled
        ? definitionValues.nspd_cadastral_field_uuid
        : null,
    };

    try {
      const res = data
        ? await updateItem({ uuid: data.uuid, ...payload })
        : await createItem({ type: MODEL_BY_PATH[type], ...payload });

      if (data?.uuid && !WITHOUT_TEMPLATES.includes(type)) {
        await saveTemplates(data.uuid, nextTemplates);
      }

      onSave(res);
      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const onOk = () => {
    form
      .validateFields()
      .then(handleSave)
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  return (
    <Modal
      maskClosable={false}
      title={
        data
          ? `Редактировать ${MODAL_TITLES[type]}`
          : `Добавить ${MODAL_TITLES[type]}`
      }
      open={visible}
      onOk={onOk}
      onCancel={handleCancel}
      okText={data ? "Сохранить" : "Добавить"}
      cancelText="Отмена"
      okButtonProps={{ loading: isSaving, disabled: loading }}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={data}
          onFinish={handleSave}
        >
          <Form.Item
            name="name"
            label="Название"
            rules={[
              { required: true, message: "Пожалуйста, введите название" },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={5} />
          </Form.Item>

          {isEdit && !WITHOUT_TEMPLATES.includes(type) && (
            <Form.Item name="templates" label="Шаблоны">
              <CustomSelect
                mode="multiple"
                placeholder="Начните вводить"
                options={templates}
                showSearch
                allowClear
                optionFilterProp="label"
                fieldNames={{ value: "uuid" }}
                onDeselect={handleTemplateDeselect}
                onClear={handleTemplatesClear}
              />
            </Form.Item>
          )}

          {type === "objects" && (
            <>
              <Form.Item name="measure_unit" label="Единицы измерения">
                <Input />
              </Form.Item>

              <Form.Item
                name="use_permission_rules"
                label="Расширенный доступ"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </>
          )}

          {isEdit && (
            <Form.Item
              name="is_nspd_enabled"
              label="Актуализация данных из НСПД"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}

          {!isEdit && (
            <Typography.Text type="secondary">
              Актуализацию данных из НСПД можно будет настроить после создания и
              добавления поля кадастрового номера.
            </Typography.Text>
          )}

          {isEdit && useNSPD && (
            <>
              <Form.Item
                name="nspd_category_uuid"
                label="Категория ключей НСПД"
                rules={[
                  {
                    required: true,
                    message: "Выберите категорию ключей НСПД",
                  },
                ]}
              >
                <CustomSelect
                  showSearch
                  allowClear
                  options={keyCategories}
                  placeholder="Выберите категорию"
                  optionFilterProp="label"
                  fieldNames={{ label: "name", value: "uuid" }}
                />
              </Form.Item>

              <Form.Item
                name="nspd_cadastral_field_uuid"
                label="Поле кадастрового номера"
                rules={[
                  {
                    required: true,
                    message: "Выберите поле кадастрового номера",
                  },
                ]}
              >
                <CustomSelect
                  showSearch
                  allowClear
                  options={cadastreFieldOptions}
                  placeholder="Выберите поле типа строка или маска"
                  optionFilterProp="label"
                />
              </Form.Item>
            </>
          )}
          {showDisableNSPDWarning && (
            <Typography.Text type="secondary">
              После сохранения возможность актуализации данных из НСПД будет
              отключена, а связи полей объекта с ключами НСПД будут очищены.
            </Typography.Text>
          )}

          {error && <Text type="danger">{error}</Text>}
        </Form>
      </Spin>
    </Modal>
  );
};

export default DefinitionModal;
